import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Calendar,
  Loader2,
  MessageCircle,
  RefreshCw,
  Search,
  Stethoscope,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAttendedDoctors } from '@/hooks/useAttendedDoctors';
import {
  conversationAvailabilityLabel,
  filterAttendedDoctors,
} from '@/lib/attendedDoctors';
import { formatDoctorSpecialty } from '@/lib/doctorSpecialty';

export const AttendedDoctorsList: React.FC = () => {
  const navigate = useNavigate();
  const { doctors, isLoading, error, refresh, startConversation } =
    useAttendedDoctors();
  const [search, setSearch] = useState('');
  const [startingDoctorId, setStartingDoctorId] = useState<string | null>(null);

  const filtered = useMemo(
    () => filterAttendedDoctors(doctors, search),
    [doctors, search],
  );

  const handleStartConversation = async (doctorId: string, available: boolean, reason: string | null) => {
    if (!available) {
      toast.error(reason || 'Este médico não está disponível para conversa.');
      return;
    }

    try {
      setStartingDoctorId(doctorId);
      const result = await startConversation(doctorId);
      toast.success(
        result.reused
          ? 'Abrindo conversa existente...'
          : 'Conversa iniciada com sucesso.',
      );
      navigate(`/chat/${result.appointmentId}`);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : 'Não foi possível iniciar a conversa.',
      );
    } finally {
      setStartingDoctorId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-10">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50 rounded-xl text-center">
        <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-full text-amber-600 dark:text-amber-400 mb-3">
          <AlertCircle className="h-6 w-6" />
        </div>
        <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
          Não foi possível carregar o histórico
        </h3>
        <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 max-w-xs">
          {error}
        </p>
        <button
          type="button"
          onClick={() => refresh()}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-900/60"
        >
          <RefreshCw className="h-4 w-4" />
          Tentar novamente
        </button>
      </div>
    );
  }

  if (doctors.length === 0) {
    return (
      <div className="text-center p-10 bg-slate-50/50 dark:bg-neutral-900/50 rounded-2xl border-2 border-dashed border-slate-200 dark:border-neutral-800">
        <div className="mx-auto h-16 w-16 bg-teal-50 dark:bg-teal-900/20 rounded-full flex items-center justify-center mb-4">
          <Users className="h-8 w-8 text-teal-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
          Você ainda não foi atendido por médicos
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-sm mx-auto leading-relaxed">
          Após a conclusão de uma consulta ou plantão, os médicos que te atenderam
          aparecerão aqui para você retomar o contato.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/buscar-medicos')}
            className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-teal-500/20 transition-all"
          >
            Agendar consulta
          </button>
          <button
            type="button"
            onClick={() => navigate('/on-demand/triage')}
            className="px-6 py-2.5 bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-neutral-700 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-neutral-700 transition-all"
          >
            Falar com plantonista
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome ou especialidade..."
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">
          Nenhum médico encontrado para “{search.trim()}”.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((item) => {
            const name = item.doctor?.name || 'Médico';
            const specialty = formatDoctorSpecialty(item.doctor?.specialty);
            const available = item.conversationAvailability === 'available';
            const isStarting = startingDoctorId === item.doctorId;

            return (
              <div
                key={item.id}
                className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="h-10 w-10 shrink-0 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400 font-bold">
                      {name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-gray-900 dark:text-white truncate">
                        {name}
                      </h4>
                      {specialty && (
                        <p className="text-xs text-teal-700 dark:text-teal-400 mt-0.5 flex items-center gap-1">
                          <Stethoscope className="h-3 w-3" />
                          {specialty}
                        </p>
                      )}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] px-2 py-1 rounded-full font-medium whitespace-nowrap ${
                      available
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                    }`}
                  >
                    {conversationAvailabilityLabel(item.conversationAvailability)}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                    <Calendar className="h-3.5 w-3.5 mr-2 shrink-0" />
                    <span>
                      Último atendimento:{' '}
                      {new Date(item.lastEncounterDate).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  {!available && item.conversationBlockedReason && (
                    <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                      {item.conversationBlockedReason}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  disabled={isStarting}
                  onClick={() =>
                    handleStartConversation(
                      item.doctorId,
                      available,
                      item.conversationBlockedReason,
                    )
                  }
                  className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60 ${
                    available
                      ? 'bg-teal-600 hover:bg-teal-700 text-white'
                      : 'bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {isStarting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MessageCircle className="h-4 w-4" />
                  )}
                  Iniciar conversa
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
